import * as React from "react";
import { useLanguage } from "./Language";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
  FormControlLabel,
  FormLabel,
  IconButton,
  OutlinedTextFieldProps,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { v4 as uuidv4 } from "uuid";
import { BranchLimitControl } from "./BranchLimitControl";
import { BranchLimit, useBranchLimits } from "./BranchLimits";
import { useTheme } from "@mui/material/styles";

export enum SearchTypeKey {
  BeginsWith = "BeginsWith",
  Contains = "Contains",
  ExactMatch = "ExactMatch",
  NotContains = "NotContains",
}

export interface SearchToken {
  searchString: string;
  type: SearchTypeKey;
  field: SearchFieldKey;
}

interface AdvancedSearchString {
  isAnd: boolean;
  isReadingLevel: boolean;
  searchTokens: SearchToken[];
}

export interface ReadingLevelFilters {
  id?: number;
  bookLevelStart: number;
  bookLevelEnd: number;
  interestLevel: string;
  lexileStart: number;
  lexileEnd: number;
}

export interface AdvancedSearchEvent {
  searchString: AdvancedSearchString;
  // searchType: any;
  readingLevelFilters?: ReadingLevelFilters;
  branchFilters?: readonly BranchLimit["identifier"][];
}

export enum SearchFieldKey {
  AnyField = "AnyField",
  Title = "Title",
  Author = "Author",
  Series = "Series",
  Subject = "Subject",
  Note = "Note",
  Tag = "Tag",
  ISBN = "ISBN",
  ISSN = "ISSN",
  UPC = "UPC",
  CallNumber = "CallNumber",
}

interface SearchFieldConfig {
  defaultSearchType: SearchTypeKey;
  searchTypes: SearchTypeKey[];
}

export interface BooleanSearchFormProps {
  maxRows?: number;
  allowTagging?: boolean;
  onClickSearch: (search: AdvancedSearchEvent) => void;
}

interface SearchRow {
  id: string;
  field: SearchFieldKey;
  type: SearchTypeKey;
  term: string;
}

interface RequiredTextFieldProps extends OutlinedTextFieldProps {
  submitted: boolean;
}

export const RequiredTextField: React.FC<RequiredTextFieldProps> = ({
  submitted,
  onChange,
  error: errorProp,
  helperText: helperTextProp,
  ...props
}) => {
  const [touched, setTouched] = React.useState(false);
  const [invalid, setInvalid] = React.useState(true);
  const { translate } = useLanguage();
  const requiredFieldError = translate("requiredFieldError");
  const error = ((touched || submitted) && invalid) || errorProp;
  const helperText =
    (touched || submitted) && invalid
      ? requiredFieldError
      : helperTextProp || "";

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInvalid(value.trim().length === 0);

    if (onChange) {
      onChange(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const value = event.target.value;
    setInvalid(value.trim().length === 0);

    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  return (
    <TextField
      required
      error={error}
      helperText={helperText}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  );
};

export const BooleanSearchForm: React.FC<BooleanSearchFormProps> = ({
  onClickSearch,
  maxRows = 5,
  allowTagging = false,
}) => {
  const { translate } = useLanguage();
  const { selectedBranchIds } = useBranchLimits();
  const theme = useTheme();
  const isLargeMobileAndBelow = useMediaQuery(theme.breakpoints.down("sm"));
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const [logicalOperator, setLogicalOperator] = React.useState<string>("all");

  const onChangeLogicalOperator = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLogicalOperator((event.target as HTMLInputElement).value);
  };

  const searchFieldLabel: Record<SearchFieldKey, string> = {
    [SearchFieldKey.AnyField]: translate("advancedSearch_anyField"),
    [SearchFieldKey.Title]: translate("advancedSearch_title"),
    [SearchFieldKey.Author]: translate("advancedSearch_author"),
    [SearchFieldKey.Series]: translate("advancedSearch_series"),
    [SearchFieldKey.Subject]: translate("advancedSearch_subject"),
    [SearchFieldKey.Note]: translate("advancedSearch_note"),
    [SearchFieldKey.Tag]: translate("advancedSearch_tag"),
    [SearchFieldKey.ISBN]: translate("advancedSearch_isbn"),
    [SearchFieldKey.ISSN]: translate("advancedSearch_issn"),
    [SearchFieldKey.UPC]: translate("advancedSearch_upc"),
    [SearchFieldKey.CallNumber]: translate("localCallNumber"),
  };
  const searchTypeLabel: Record<SearchTypeKey, string> = {
    [SearchTypeKey.BeginsWith]: translate("beginsWith"),
    [SearchTypeKey.Contains]: translate("contains"),
    [SearchTypeKey.ExactMatch]: translate("exactlyMatches"),
    [SearchTypeKey.NotContains]: translate("doesNotContain"),
  };

  const searchFieldSearchTypes: Record<SearchFieldKey, SearchFieldConfig> = {
    [SearchFieldKey.AnyField]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [SearchTypeKey.Contains, SearchTypeKey.NotContains],
    },
    [SearchFieldKey.Title]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [
        SearchTypeKey.BeginsWith,
        SearchTypeKey.Contains,
        SearchTypeKey.ExactMatch,
        SearchTypeKey.NotContains,
      ],
    },
    [SearchFieldKey.Author]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [
        SearchTypeKey.BeginsWith,
        SearchTypeKey.Contains,
        SearchTypeKey.ExactMatch,
        SearchTypeKey.NotContains,
      ],
    },
    [SearchFieldKey.Series]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [
        SearchTypeKey.BeginsWith,
        SearchTypeKey.Contains,
        SearchTypeKey.ExactMatch,
        SearchTypeKey.NotContains,
      ],
    },
    [SearchFieldKey.Subject]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [
        SearchTypeKey.BeginsWith,
        SearchTypeKey.Contains,
        SearchTypeKey.ExactMatch,
        SearchTypeKey.NotContains,
      ],
    },
    [SearchFieldKey.Note]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [SearchTypeKey.Contains, SearchTypeKey.NotContains],
    },
    [SearchFieldKey.Tag]: {
      defaultSearchType: SearchTypeKey.Contains,
      searchTypes: [
        SearchTypeKey.BeginsWith,
        SearchTypeKey.Contains,
        SearchTypeKey.ExactMatch,
        SearchTypeKey.NotContains,
      ],
    },
    [SearchFieldKey.ISBN]: {
      defaultSearchType: SearchTypeKey.BeginsWith,
      searchTypes: [SearchTypeKey.BeginsWith, SearchTypeKey.ExactMatch],
    },
    [SearchFieldKey.ISSN]: {
      defaultSearchType: SearchTypeKey.BeginsWith,
      searchTypes: [SearchTypeKey.BeginsWith, SearchTypeKey.ExactMatch],
    },
    [SearchFieldKey.UPC]: {
      defaultSearchType: SearchTypeKey.BeginsWith,
      searchTypes: [SearchTypeKey.BeginsWith, SearchTypeKey.ExactMatch],
    },
    [SearchFieldKey.CallNumber]: {
      defaultSearchType: SearchTypeKey.BeginsWith,
      searchTypes: [SearchTypeKey.BeginsWith],
    },
  };

  const defaultSearchField = SearchFieldKey.Title;
  const defaultSearchType =
    searchFieldSearchTypes[defaultSearchField].defaultSearchType;

  const handleChange = (event: SelectChangeEvent, termId: string) => {
    const searchFieldKey = event.target.value as SearchFieldKey;
    const updatedTerms = [...terms];
    updatedTerms.forEach((term) => {
      if (term.id === termId) {
        term.field = searchFieldKey;
        term.type = searchFieldSearchTypes[searchFieldKey].defaultSearchType;
      }
    });
    setTerms(updatedTerms);
  };

  const handleSearchTypeChange = (event: SelectChangeEvent, termId: string) => {
    const searchType = event.target.value as SearchTypeKey;
    const updatedTerms = [...terms];
    updatedTerms.forEach((term) => {
      if (term.id === termId) {
        term.type = searchType;
      }
    });
    setTerms(updatedTerms);
  };

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    termId: string
  ) => {
    const searchTerm = event.target.value as SearchTypeKey;
    const updatedTerms = [...terms];
    updatedTerms.forEach((term) => {
      if (term.id === termId) {
        term.term = searchTerm;
      }
    });
    setTerms(updatedTerms);
  };

  // TODO integrate with Formik or React Hook Form and Yup
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    // Can't use this because aria-invalid="true" won't happen on some fields until
    // submitted state settles
    // const form = event.target as HTMLFormElement;
    // const errorInputs = form.querySelectorAll('input[aria-invalid="true"]');

    // if (errorInputs.length > 0) {
    //   console.log("Form has invalid fields");
    //   return;
    // }

    // iterate the terms instead and ensure none are empty
    if (terms.find(({ term }) => term.trim().length === 0)) {
      console.log("Form has invalid fields");
      return;
    }

    // this matches a pre-existing event structure for advanced search
    const advancedSearchEvent: AdvancedSearchEvent = {
      searchString: {
        isAnd: logicalOperator === "all",
        isReadingLevel: false,
        searchTokens: terms.map((value) => ({
          searchString: value.term,
          type: value.type,
          field: value.field,
        })),
      },
      branchFilters: selectedBranchIds,
    };
    onClickSearch(advancedSearchEvent);
  };

  const getDefaultTerms = (): SearchRow[] => {
    return [
      {
        id: uuidv4(),
        field: SearchFieldKey.Title,
        term: "",
        type: defaultSearchType,
      },
    ];
  };

  const [terms, setTerms] = React.useState<SearchRow[]>(getDefaultTerms());

  const addTermRow = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (terms.length < maxRows) {
      const field = SearchFieldKey.Subject;
      const type = searchFieldSearchTypes[field].defaultSearchType;
      const id = uuidv4();
      // calling setTerms renders a new component with autoFocus
      // MUI Button doesn't handle blurring and removing the ripple in this case
      event.currentTarget.blur();
      setTerms([...terms, { id, field, type, term: "" }]);
    }
  };

  const removeTermRow = (id: string) => {
    setTerms(terms.filter((term) => term.id !== id));
  };

  const onClickClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    // calling setTerms renders a new component with autoFocus
    // MUI Button doesn't handle blurring and removing the ripple in this case
    event.currentTarget.blur();
    setSubmitted(false);
    setTerms(getDefaultTerms);
  };

  const renderTermRows = () => {
    return terms.map((term, index) => (
      <Box key={term.id}>
        <Stack direction={isLargeMobileAndBelow ? "column" : "row"} gap={2}>
          <Box sx={{ minWidth: "136px" }}>
            <FormControl fullWidth>
              <InputLabel id={`${term.id}-field-label`}>
                {translate("field")}
              </InputLabel>
              <Select
                labelId={`${term.id}-field-label`}
                label={translate("field")}
                value={term.field}
                name="field"
                onChange={(event) => handleChange(event, term.id)}
              >
                {Object.entries(searchFieldLabel).map(([key, value]) => {
                  if (allowTagging || key !== SearchFieldKey.Tag) {
                    return (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    );
                  }
                  return "";
                })}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: "168px" }}>
            <FormControl fullWidth>
              <InputLabel id={`${term.id}-type-label`}>
                {translate("type")}
              </InputLabel>
              <Select
                labelId={`${term.id}-type-label`}
                label={translate("type")}
                value={term.type}
                name="type"
                onChange={(event) => handleSearchTypeChange(event, term.id)}
              >
                {term.type &&
                  searchFieldSearchTypes[term.field].searchTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {searchTypeLabel[type]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Stack direction="row" alignItems="flex-start">
            <RequiredTextField
              sx={{ flexGrow: 1 }}
              label={translate("term")}
              name="term"
              value={term.term}
              onChange={(event) => handleSearchTermChange(event, term.id)}
              required
              autoFocus
              submitted={submitted}
              variant="outlined"
            />

            <Tooltip title={translate("removeTerm")}>
              <span>
                <IconButton
                  sx={{ paddingTop: "12px" }}
                  aria-label={translate("removeTerm")}
                  onClick={() => removeTermRow(term.id)}
                  disabled={terms.length < 2}
                  color="warning"
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        {index < terms.length - 1 && (
          <Typography aria-hidden="true" variant="overline" display="block">
            {logicalOperator === "all"
              ? translate("and")
              : translate("or").toUpperCase()}
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit} noValidate>
        <Stack direction="column" spacing={2}>
          <BranchLimitControl></BranchLimitControl>
          <Box>
            <FormControl>
              <FormLabel id="logicalOperator-group">
                {translate("include")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="logicalOperator-group"
                name="logicalOperator"
                value={logicalOperator}
                onChange={onChangeLogicalOperator}
              >
                <FormControlLabel
                  value="any"
                  control={<Radio />}
                  label={translate("orOperator")}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={translate("andOperator")}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          {renderTermRows()}
          <Box>
            <Button
              onClick={addTermRow}
              startIcon={<AddIcon />}
              disabled={terms.length >= maxRows}
            >
              {translate("addTerm")}
            </Button>
          </Box>

          <Stack direction="row" gap={1}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
            >
              {translate("search")}
            </Button>
            <Button onClick={onClickClear}>{translate("clear")}</Button>
          </Stack>
        </Stack>
      </form>
    </React.Fragment>
  );
};
